import React, { useState, useContext, useEffect, useRef } from "react";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";
import Map, { Marker, Source, Layer } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { TbPlus, TbMinus, TbCaretUpFilled } from "react-icons/tb";
import { HiddenMapGeolocateControl, MapGeolocateButton } from "./MapGeolocate";
import { BsLayers } from "react-icons/bs";
import { Title, Modal } from "../elements/Popup";
import {
  NotificationContext,
  ResponsiveTableView
} from "@solsys/solsys-reactjs";

const gqlQuery = gql`
  query entityList($prospect_list_request: prospect_list_request,
  $crane_list_request: crane_list_request)
  {
    prospect(prospect_list_request: $prospect_list_request) {
      edges {
        id
        name
        longitude
        latitude
      }
      totalCount
    }
    crane(crane_list_request: $crane_list_request) {
      edges {
        id
        name
        longitude
        latitude
      }
      totalCount
    }
  }
`;

const craneMutation = gql`
  mutation craneMutate($crane_mutation_request: MutationInput) {
    crane(crane_mutation_request: $crane_mutation_request) {
      id
      isNew
    }
  }
`;

const prospectMutation = gql`
  mutation prospectMutate($prospect_mutation_request: MutationInput) {
    prospect(prospect_mutation_request: $prospect_mutation_request) {
      id
      isNew
    }
  }
`;
const MultiView = ({ style }) => {
  const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "RUNTIME_REPLACE_REACT_APP_MAPBOX_TOKEN";
  const history = useHistory();
  const { addNotification } = useContext(NotificationContext);
  const [entityType, setEntityType] = useState('prospect');
  const [prospectList, setProspectList] = useState([]);
  const [craneList, setCraneList] = useState([]);
  const [renderList, setRenderList] = useState([]);
  const [viewState, setViewState] = useState({
    latitude: 39.976875,
    longitude: -82.991449,
    zoom: 1,
  });
  const [craneId, setCraneId] = useState(null);
  const [prospectId, setProspectId] = useState(null);
  const [name, setName] = useState(null);
  const [lon, setLon] = useState(null);
  const [lat, setLat] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [status, setStatus] = useState(null);
  const [mapViewStyle, setMapViewStyle] = useState('mapbox://styles/mapbox/outdoors-v12');
  const [mapStyle, setMapStyle] = useState('outdoors');
  const [showStyles, setShowStyles] = useState(false);
  const [showRoads, setShowRoads] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [showBoundaries, setShowBoundaries] = useState(true);
  const [showLegend, setShowLegend] = useState(true);
  const [showProspect, setShowProspect] = useState(true);
  const [showCrane, setShowCrane] = useState(true);
  const longPressTimer = useRef(null);
  const startPosition = useRef(null);
  const isLongPressValid = useRef(false); // Flag to track movement
  const mapRef = useRef(null);
  const geolocateRef = useRef(null);
  const [geoJsonData, setGeoJsonData] = useState({ type: 'FeatureCollection', features: [] });

  const entityTypes = [
    {
      text: 'Prospect',
      value: 'prospect'
    },
    {
      text: 'Crane',
      value: 'crane'
    }
  ]

  let variables = {
    prospect_list_request: {
      sorting: [{ field: "id", direction: "ASC" }],
    },
    crane_list_request: {
      sorting: [{ field: "id", direction: "ASC" }],
    },
  };

  const [refetch] = useLazyQuery(gqlQuery, {
    variables,
    pollInterval: 5000,
    onCompleted: (localData) => {
      setProspectList(localData?.prospect?.edges ?? []);
      setCraneList(localData?.crane?.edges ?? []);
    },
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
  });

  useEffect(() => {
    refetch();
  }, [])

  useEffect(() => {
    if (mapStyle == 'streets') setMapViewStyle('mapbox://styles/mapbox/streets-v12');
    if (mapStyle == 'satellite') setMapViewStyle('mapbox://styles/mapbox/satellite-v9');
    if (mapStyle == 'outdoors') setMapViewStyle('mapbox://styles/mapbox/outdoors-v12');
  }, [mapStyle])

  useEffect(() => {
    const prospect = prospectList.find(prospect =>
      isCoordinateValid(prospect.latitude, prospect.longitude)
    );
    if (prospect) {
      setViewState({ latitude: prospect.latitude, longitude: prospect.longitude, zoom: 13 });
      return;
    }
    const crane = craneList.find(crane =>
      isCoordinateValid(crane.latitude, crane.longitude)
    );
    if (crane) {
      setViewState({ latitude: crane.latitude, longitude: crane.longitude, zoom: 13 });
      return;
    }
  }, [prospectList, craneList])

  // Build clustered GeoJSON from lists
  useEffect(() => {
    const features = [];
    if (showProspect) {
      for (const p of prospectList) {
        if (isCoordinateValid(p.latitude, p.longitude)) {
          features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
            properties: { id: p.id, name: p.name, type: 'prospect' }
          });
        }
      }
    }
    if (showCrane) {
      for (const c of craneList) {
        if (isCoordinateValid(c.latitude, c.longitude)) {
          features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [c.longitude, c.latitude] },
            properties: { id: c.id, name: c.name, type: 'crane' }
          });
        }
      }
    }
    setGeoJsonData({ type: 'FeatureCollection', features });
  }, [prospectList, craneList, showProspect, showCrane]);

  const [mutateProspect, mutateProspectInfo] = useMutation(prospectMutation, {
    variables: {
      prospect_mutation_request: {
        id: prospectId,
        properties: [
          { key: "name", string: name },
          { key: "longitude", float: parseFloat(lon) },
          { key: "latitude", float: parseFloat(lat) }
        ],
      },
    },
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
    onCompleted: (response, originalRequestVariables) => {
      // refetch();
      let prospect = response?.prospect;
      if (!prospect.isNew) {
        setProspectList(prevItems =>
          prevItems.map(item =>
            item.id == prospect.id
              ? { ...item, name: name, longitude: lon, latitude: lat } // Update the specific item
              : item // Keep other items unchanged
          )
        );
      } else {
        setProspectList([...prospectList, { id: prospect.id, name: name, longitude: lon, latitude: lat, __typename: 'prospect' }]);
      }
      addNotification("good", "Prospect saved successfully");
      setShowPopup(false);
      clear();
    },
  });

  const [mutateCrane, mutateCraneInfo] = useMutation(craneMutation, {
    variables: {
      crane_mutation_request: {
        id: craneId,
        properties: [
          { key: "name", string: name },
          { key: "longitude", float: parseFloat(lon) },
          { key: "latitude", float: parseFloat(lat) }
        ],
      },
    },
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
    onCompleted: (response, originalRequestVariables) => {
      let crane = response?.crane;
      // refetch();
      if (!crane.isNew) {
        setCraneList(prevItems =>
          prevItems.map(item =>
            item.id == crane.id
              ? { ...item, name: name, longitude: lon, latitude: lat } // Update the specific item
              : item // Keep other items unchanged
          )
        );
      } else {
        setCraneList([...craneList, { id: crane.id, name: name, longitude: lon, latitude: lat, __typename: 'crane' }]);
      }
      addNotification("good", "Crane saved successfully");
      setShowPopup(false);
      clear();
    },
  });

  const clear = () => {
    setProspectId(null);
    setCraneId(null);
    setName('');
    setLon('');
    setLat('');
  }

  const editEntity = () => {
    if (!prospectId && !craneId) return;
    const entity = renderList.find((item) => item.id == prospectId || item.id == craneId);
    setName(entity.name);
    setLon(entity.longitude);
    setLat(entity.latitude);
    setStatus('edit');
  }

  const handleLongPressStart = (e) => {
    const { lng, lat } = e.lngLat;

    startPosition.current = { lng, lat }; // Store start position
    isLongPressValid.current = true; // Reset flag

    longPressTimer.current = setTimeout(() => {
      if (isLongPressValid.current) {
        setShowPopup(true);
        setCraneId(null);
        setProspectId(null);
        setStatus('select');
      }
    }, 800); // Show popup only if no movement
  };

  const handleMove = (e) => {
    setViewState(e.viewState);
    isLongPressValid.current = false;
  };

  const handleLongPressEnd = () => {
    clearTimeout(longPressTimer.current);
    isLongPressValid.current = false;
  };

  const handleMarkerClick = (type, id) => {
    const path = `/${type}/edit/${id}?sortBy=id&sortDirection=ASC&tab=Map`;
    history.push(path);
  }

  const changeZoom = (value) => {
    if (mapRef.current) {
      const map = mapRef.current.getMap(); // Get the Mapbox instance
      const newZoom = map.getZoom() + value;

      map.flyTo({
        zoom: newZoom,
        duration: 1000, // Smooth animation duration in ms
        essential: true, // Ensures animation always happens
      });
    }
  }

  const toggleLegend = () => setShowLegend(!showLegend);
  const toggleProspect = () => setShowProspect(!showProspect);
  const toggleCrane = () => setShowCrane(!showCrane);

  let loadingMutation = mutateProspectInfo.loading && mutateCraneInfo.loading;

  const isCoordinateValid = (lat, lon) =>
    lat != null && lon != null && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;

  const onMapClick = (event) => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const features = map.queryRenderedFeatures(event.point, {
      layers: ['clusters', 'unclustered-point']
    });
    if (!features.length) return;
    const feature = features[0];

    // If cluster, zoom in/expand
    if (feature.properties && feature.properties.cluster) {
      const clusterId = feature.properties.cluster_id;
      const source = map.getSource('entities');
      if (source && source.getClusterExpansionZoom) {
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          map.easeTo({ center: feature.geometry.coordinates, zoom });
        });
      }
      return;
    }

    // Unclustered point click -> navigate
    const props = feature.properties || {};
    const type = props.type === 'crane' ? 'ecrane' : 'prospect';
    if (props.id) {
      handleMarkerClick(type, props.id);
    }
  };

  return (
    <div style={ style }>
      <div className="page-header">
        <span className="page-header-title">
          MapView
        </span>
      </div>

      <div style={ { height: 'calc(100% - 36px)' } }>
        <div label="Map" style={ { height: '100%' } }>
          <Map
            ref={ mapRef }
            { ...viewState }
            onMove={ handleMove }
            style={ { width: "100%", height: "100%" } }
            onMouseDown={ handleLongPressStart }
            onMouseUp={ handleLongPressEnd }
            onTouchStart={ handleLongPressStart }
            onTouchEnd={ handleLongPressEnd }
            mapStyle={ mapViewStyle }
            mapboxAccessToken={ MAPBOX_TOKEN }
            onClick={ onMapClick }
          >
            <HiddenMapGeolocateControl ref={ geolocateRef } />
            {/* Clustered entities source */ }
            <Source
              id="entities"
              type="geojson"
              data={ geoJsonData }
              cluster={ true }
              clusterMaxZoom={ 14 }
              clusterRadius={ 50 }
            >
              {/* Cluster circles */ }
              <Layer
                id="clusters"
                type="circle"
                filter={ ["has", "point_count"] }
                paint={ {
                  "circle-color": [
                    "step",
                    ["get", "point_count"],
                    "#51bbd6",
                    50,
                    "#f1f075",
                    100,
                    "#f28cb1"
                  ],
                  "circle-radius": [
                    "step",
                    ["get", "point_count"],
                    15,
                    50,
                    20,
                    100,
                    25
                  ]
                } }
              />
              {/* Cluster count labels */ }
              <Layer
                id="cluster-count"
                type="symbol"
                filter={ ["has", "point_count"] }
                layout={ {
                  "text-field": ["get", "point_count_abbreviated"],
                  "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
                  "text-size": 12
                } }
                paint={ { "text-color": "#000" } }
              />
              {/* Unclustered points */ }
              <Layer
                id="unclustered-point"
                type="circle"
                filter={ ["!", ["has", "point_count"]] }
                paint={ {
                  "circle-color": [
                    "case",
                    ["==", ["get", "type"], "crane"],
                    "#ff5a5f",
                    "#2ecc71"
                  ],
                  "circle-radius": 7,
                  "circle-stroke-width": 1,
                  "circle-stroke-color": "#fff"
                } }
              />
            </Source>
            { mapStyle == 'satellite' && showRoads && <Source
              id="composite"
              type="vector"
              url="mapbox://mapbox.mapbox-streets-v8"
            >
              <Layer
                id="roads"
                type="line"
                source-layer="road"
                paint={ {
                  "line-color": "#ffd700",
                  "line-width": 1,
                } }
              />
            </Source> }

            { mapStyle == 'satellite' && showBoundaries && <Source
              id="state-borders"
              type="vector"
              url="mapbox://mapbox.mapbox-streets-v8"
            >
              <Layer
                id="state-lines"
                type="line"
                source-layer="admin"
                filter={ ["all", ["==", "admin_level", 1]] } // Corrected filter syntax
                paint={ {
                  "line-color": "#00ff00", // Green color for state lines
                  "line-width": 2,
                } }
              />
            </Source> }

            { mapStyle == 'satellite' && showLabels && <Source
              id="city-labels"
              type="vector"
              url="mapbox://mapbox.mapbox-streets-v8"
            >
              <Layer
                id="city-labels"
                type="symbol"
                source-layer="place_label"
                layout={ {
                  "text-field": "{name_en}", // Label with name
                  "text-size": 12,
                } }
                paint={ {
                  "text-color": "#ffffff", // White color for text
                  "text-halo-color": "#000000", // Black halo for visibility
                  "text-halo-width": 1,
                } }
              />
            </Source> }
            {/* Individual markers replaced by clustered source above */ }
          </Map>
          <div className="map-controls">
            <div className="navigation-control">
              <button className="zoom-in">
                <TbPlus className="zoom-in-icon" onClick={ () => changeZoom(1) } />
              </button>
              <button className="zoom-out">
                <TbMinus className="zoom-out-icon" onClick={ () => changeZoom(-1) } />
              </button>
              <MapGeolocateButton controlRef={ geolocateRef } />
            </div>
            <div className="style-control">
              <button className="style-selector" onClick={ () => setShowStyles((prev) => !prev) }>
                <BsLayers className="style-selector-icon" />
              </button>
              { showStyles && <div className="map-styles">
                <div className="main-styles">
                  <div className={ `map-style-button ${mapStyle === "streets" ? "map-style-button-active" : ""}` } onClick={ () => setMapStyle('streets') }>Streets</div>
                  <div className={ `map-style-button ${mapStyle === "outdoors" ? "map-style-button-active" : ""}` } onClick={ () => setMapStyle('outdoors') }>Outdoors</div>
                  <div className={ `map-style-button ${mapStyle === "satellite" ? "map-style-button-active" : ""}` } onClick={ () => setMapStyle('satellite') }>Satellite</div>
                </div>
                { mapStyle == 'satellite' && <div className="source-styles">
                  <div className={ `map-style-button ${showRoads ? "map-style-button-active" : ""}` } onClick={ () => setShowRoads((prev) => !prev) }>Roads</div>
                  <div className={ `map-style-button ${showLabels ? "map-style-button-active" : ""}` } onClick={ () => setShowLabels((prev) => !prev) }>Labels</div>
                  <div className={ `map-style-button ${showBoundaries ? "map-style-button-active" : ""}` } onClick={ () => setShowBoundaries((prev) => !prev) }>Boundaries</div>
                </div> }
              </div> }
            </div>
          </div>
          {/* Instructions/Legend Panel */ }
          { showLegend ? (
            <div className="legend-panel">
              <button className="close" onClick={ toggleLegend }>✖</button>
              <h3>Instruction & Legend</h3>
              <p>🖐️ <strong>Hold Gesture:</strong> Press and hold to add/edit crane or prospect.</p>
              <hr />
              <div className="legend-icon">
                <input type="checkbox" checked={ showProspect } onChange={ toggleProspect } />
                <img src="/assets/marker-prospect.png" width={ 15 } />
                <p>Prospect</p>
              </div>
              <div className="legend-icon">
                <input type="checkbox" checked={ showCrane } onChange={ toggleCrane } />
                <img src="/assets/marker-crane.png" width={ 15 } />
                <p>Crane</p>
              </div>
            </div>
          ) : (
            <div className="legend-control">
              <button className="show-legend">
                <TbCaretUpFilled className="show-legend-icon" onClick={ toggleLegend } />
              </button>
            </div>
          ) }
          <Modal isOpen={ showPopup } onClose={ () => setShowPopup(false) } width={ '250px' }>
            { status == 'select' && <>
              <h4 style={ { marginTop: '10px', textAlign: 'center' } }>Add/Edit Crane or Prospect</h4>
              <div style={ { display: 'grid', gap: '10px' } }>
                <button onClick={ () => {
                  setStatus('list');
                  setEntityType('prospect');
                  setRenderList(prospectList);
                } }>Edit</button>
                <button onClick={ () => {
                  setStatus('add');
                  setEntityType('prospect');
                  setName('');
                  setLon(startPosition.current.lng);
                  setLat(startPosition.current.lat);
                } }>Add</button>
              </div>
            </> }
            { (status == 'add' || status == 'edit') && <>
              <h4 style={ { marginTop: '10px', textAlign: 'center' } }>{ status == 'add' ? 'Add' : 'Edit' } { entityType == 'prospect' ? 'Prospect' : 'Crane' }</h4>
              <div style={ { display: 'grid', gap: '10px', padding: '0px 21px' } }>
                { status == 'add' && <div className="edit-property">
                  <label>Entity:</label>
                  <div>
                    <select
                      value={ entityType }
                      onChange={ (event) => { setEntityType(event.target.value); } }
                    >
                      { entityTypes.map((entityType) => (
                        <option key={ entityType.text } value={ entityType.value }>
                          { entityType.text }
                        </option>
                      )) }
                    </select>
                  </div>
                </div> }
                <div className="edit-property">
                  <label>Name:</label>
                  <input value={ name } onChange={ (e) => setName(e.target.value) } />
                </div>
                <div className="edit-property">
                  <label>Longitude:</label>
                  <input
                    type="number"
                    value={ lon }
                    onChange={ (e) => setLon(e.target.value) }
                  />
                </div>
                <div className="edit-property">
                  <label>Latitude:</label>
                  <input
                    type="number"
                    value={ lat }
                    onChange={ (e) => setLat(e.target.value) }
                  />
                </div>
                <div style={ { width: '100%', display: 'flex', justifyContent: 'space-around' } }>
                  <button
                    disabled={ loadingMutation }
                    onClick={ () => {
                      if (entityType == 'prospect') mutateProspect();
                      else mutateCrane();
                    } }
                  >
                    Save
                  </button>
                  <button disabled={ loadingMutation } onClick={ () => {
                    if (status == 'edit') setStatus('list');
                    else setStatus('select');
                  } }>Cancel</button>
                </div>
              </div>
            </> }
            { status == 'list' && <>
              <h4 style={ { marginTop: '10px', textAlign: 'center' } }>Edit Crane or Prospect</h4>
              <div style={ { display: 'flex', alignItems: 'center', fontSize: '20px', gap: '10px', marginBottom: '10px' } }>
                <label>Entity:</label>
                <div>
                  <select
                    value={ entityType }
                    onChange={ (event) => {
                      if (event.target.value == 'prospect') setRenderList(prospectList);
                      else setRenderList(craneList);
                      setEntityType(event.target.value);
                      clear();
                    } }
                  >
                    { entityTypes.map((entityType) => (
                      <option key={ entityType.text } value={ entityType.value }>
                        { entityType.text }
                      </option>
                    )) }
                  </select>
                </div>
              </div>
              <ResponsiveTableView
                style={ { height: '200px' } }
                headers={ [
                  {
                    label: "ID",
                  },
                  {
                    label: "Name",
                  },
                ] }
                data={ renderList.map((entity) => ({
                  id: entity.id,
                  ID: entity.id,
                  Name: entity.name,
                  selected: entityType == 'prospect' && prospectId == entity.id || entityType == 'crane' && craneId == entity.id,
                })) }
                identityColumnKey="ID"
                onRowClick={ (row) => {
                  if (entityType == 'prospect') setProspectId(row.id);
                  else setCraneId(row.id);
                } }
                totalCount={ renderList.length }
                addMoreResults={ () => {} }
              />
              <div style={ { width: '100%', display: 'flex', justifyContent: 'space-around' } }>
                <button
                  disabled={ loadingMutation }
                  onClick={ () => editEntity() }
                >
                  Edit
                </button>
                <button disabled={ loadingMutation } onClick={ () => setStatus('select') }>Cancel</button>
              </div>
            </> }
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default MultiView;
